import { useTaskQuery, useUpdateTaskDetailsMutation } from '@/queries/tasks';
import App from 'antd/es/app';
import Divider from 'antd/es/divider';
import Form from 'antd/es/form';
import Skeleton from 'antd/es/skeleton';
import Typography from 'antd/es/typography';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  TaskDescription,
  TaskEditModals,
  TaskHeader,
  TaskLayout,
  TaskMobileMetadata,
  TaskOutput,
  TaskSidebar,
} from './components';

import ContentContainer from '@components/ContentContainer';
import { useTheme } from '@components/ThemeProvider';
import { logger } from '@utils/logger';

const { Title, Paragraph } = Typography;

// Default primary color in case theme is undefined
const DEFAULT_PRIMARY_COLOR = '#499D9C';

// Styled components for loading states
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.token?.marginMD || '16px'};
  padding: ${({ theme }) => theme.token?.paddingLG || '24px'};
`;

const SkeletonHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.token?.marginLG || '24px'};
`;

const SkeletonLayout = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.token?.marginLG || '24px'};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SkeletonMain = styled.div`
  flex: 1;
`;

const SkeletonSidebar = styled.div`
  width: 300px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TaskDetailsPage = () => {
  const { taskId, teamId, projectId } = useParams<{
    taskId: string;
    teamId: string;
    projectId: string;
  }>();
  const { message } = App.useApp();
  const { themeToken } = useTheme();
  const primaryColor = themeToken?.colorPrimary || DEFAULT_PRIMARY_COLOR;

  // State for modals
  const [nameModalVisible, setNameModalVisible] = useState(false);
  const [descriptionModalVisible, setDescriptionModalVisible] = useState(false);
  const [metadataModalVisible, setMetadataModalVisible] = useState(false);

  // Form instances
  const [nameForm] = Form.useForm();
  const [descriptionForm] = Form.useForm();
  const [metadataForm] = Form.useForm();

  // Fetch task data
  const { data: task, isLoading: isTaskLoading } = useTaskQuery(
    taskId as string,
    !!taskId,
  );

  // Update task mutation
  const updateTaskMutation = useUpdateTaskDetailsMutation(taskId as string);

  // Handle opening edit name modal
  const handleEditName = () => {
    nameForm.setFieldsValue({ name: task?.title || '' });
    setNameModalVisible(true);
  };

  // Handle opening edit description modal
  const handleEditDescription = () => {
    descriptionForm.setFieldsValue({
      description: task?.description || '',
    });
    setDescriptionModalVisible(true);
  };

  // Handle opening edit metadata modal
  const handleEditMetadata = () => {
    // Convert metadata array to form fields
    const metadataValues: Record<string, string> = {};
    Object.entries(task?.metadata ?? {}).forEach(([key, value]) => {
      metadataValues[key] = value;
    });
    metadataForm.setFieldsValue(metadataValues);
    setMetadataModalVisible(true);
  };

  // Handle starting task
  const handleStartTask = async () => {
    try {
      await updateTaskMutation.mutateAsync({ shouldStart: true });
      void message.success('Task started successfully');
    } catch (error) {
      logger.error({ taskId }, 'Failed to start task', { error });
      void message.error('Failed to start task');
    }
  };

  // Handle name form submission
  const handleTitleSubmit = async (values: { title: string }) => {
    try {
      await updateTaskMutation.mutateAsync({ title: values.title });
      setNameModalVisible(false);
      void message.success('Task name updated successfully');
    } catch (error) {
      logger.error({ taskId }, 'Failed to update task name', { error });
      void message.error('Failed to update task name');
    }
  };

  // Handle description form submission
  const handleDescriptionSubmit = async (values: { description: string }) => {
    try {
      await updateTaskMutation.mutateAsync({ description: values.description });
      setDescriptionModalVisible(false);
      void message.success('Task description updated successfully');
    } catch (error) {
      logger.error({ taskId }, 'Failed to update task description', { error });
      void message.error('Failed to update task description');
    }
  };

  // Handle metadata form submission
  const handleMetadataSubmit = async (values: Record<string, string>) => {
    try {
      await updateTaskMutation.mutateAsync({
        metadata: values,
      });
      setMetadataModalVisible(false);
      void message.success('Task metadata updated successfully');
    } catch (error) {
      logger.error({ taskId }, 'Failed to update task metadata', { error });
      void message.error('Failed to update task metadata');
    }
  };

  // Render loading skeleton
  const renderLoadingSkeleton = () => (
    <ContentContainer showFullWidthToggle={false} forceFullWidth>
      <ContentContainer.Header
        title={
          <SkeletonHeader>
            <Skeleton.Input active style={{ width: '60%', height: 32 }} />
          </SkeletonHeader>
        }
      />
      <SkeletonLayout>
        <SkeletonMain>
          <LoadingContainer>
            <Skeleton.Input active style={{ width: '40%', height: 24 }} />
            <Skeleton active paragraph={{ rows: 3 }} title={false} />
            <Divider style={{ margin: '8px 0' }} />

            <Skeleton.Input active style={{ width: '30%', height: 24 }} />
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
            <Divider style={{ margin: '8px 0' }} />

            <Skeleton.Input active style={{ width: '35%', height: 24 }} />
            <Skeleton active paragraph={{ rows: 4 }} title={false} />
          </LoadingContainer>
        </SkeletonMain>
        <SkeletonSidebar>
          <LoadingContainer>
            <Skeleton.Input active style={{ width: '50%', height: 24 }} />
            <Skeleton active paragraph={{ rows: 6 }} title={false} />
          </LoadingContainer>
        </SkeletonSidebar>
      </SkeletonLayout>
    </ContentContainer>
  );

  if (!taskId || !teamId || !projectId || isTaskLoading) {
    return renderLoadingSkeleton();
  }

  if (!task) {
    return (
      <ContentContainer showFullWidthToggle={false} forceFullWidth>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={4}>Task not found</Title>
          <Paragraph>The requested task could not be found.</Paragraph>
        </div>
      </ContentContainer>
    );
  }

  // Render main content
  const renderMainContent = () => (
    <>
      <TaskMobileMetadata />

      <TaskDescription
        description={task.description || ''}
        onEditDescription={handleEditDescription}
      />

      <Divider style={{ margin: '0' }} />

      <TaskOutput taskId={taskId} teamId={teamId} projectId={projectId} />
      <Divider style={{ margin: '0' }} />
    </>
  );

  return (
    <ContentContainer showFullWidthToggle={false} forceFullWidth>
      <ContentContainer.Header
        title={
          <TaskHeader
            title={task.title || ''}
            onEditTitle={handleEditName}
            colorPrimary={primaryColor}
            onStartTask={handleStartTask}
          />
        }
      />
      <TaskLayout
        main={renderMainContent()}
        sidebar={
          <TaskSidebar
            createdAt={task.createdAt}
            updatedAt={task.updatedAt}
            metadata={task.metadata}
            onEditMetadata={handleEditMetadata}
            resources={[]}
            status={'TODO'}
          />
        }
      />

      <TaskEditModals
        nameModalVisible={nameModalVisible}
        descriptionModalVisible={descriptionModalVisible}
        metadataModalVisible={metadataModalVisible}
        nameForm={nameForm}
        descriptionForm={descriptionForm}
        metadataForm={metadataForm}
        metadata={task.metadata}
        onNameModalCancel={() => setNameModalVisible(false)}
        onDescriptionModalCancel={() => setDescriptionModalVisible(false)}
        onMetadataModalCancel={() => setMetadataModalVisible(false)}
        onTitleSubmit={handleTitleSubmit}
        onDescriptionSubmit={handleDescriptionSubmit}
        onMetadataSubmit={handleMetadataSubmit}
        isSubmitting={updateTaskMutation.isPending}
      />
    </ContentContainer>
  );
};

export default TaskDetailsPage;
